/**
 * Vue entry point.
 * @author bmk11u
 */
// import Vue from 'vue'
import { createApp } from 'vue'
import approuter from './router'
import VueCookies from 'vue-cookies'
import App from './App.vue'
import 'bootstrap'
import 'bootstrap/dist/css/bootstrap.min.css'
import './assets/style.scss'
import './scss/custom.scss'
import { library } from '@fortawesome/fontawesome-svg-core'
import { faArrowLeft, faCalendarAlt, faMapMarkerAlt, faCalendarPlus, faBuilding, faBell, faDownload, faSearch, faExternalLink, faPlus, faMinus,
  faChevronLeft, faChevronCircleLeft, faChevronCircleRight, faLink, faCog, faClose, faInfoCircle, faSignOutAlt, faHome, faBars, faCircle, faDesktop,
  faVoteYea, faPhoneAlt, faEnvelope, faBookReader, faChevronRight, faChevronDown, faClock, faUser, faWalking,
  faCar, faWheelchair, faBicycle, faPoundSign, faRestroom, faCoffee, faCamera, faWrench, faShower, faWifi, faPrint,
  faLaptop, faArrowRight, faFileSignature, faExclamationCircle, faSync, faMedkit, faX,
  faTimesCircle, faEllipsisH, faQuestionCircle, faComment, faUsers, faHeartbeat, faTint, faBaby, faIcons, faCalendarCheck, faLocationDot, faCheck, faDiamondTurnRight, faArrowUpRightFromSquare, faLocationCrosshairs, faElevator } from '@fortawesome/free-solid-svg-icons'
import { faPlayCircle, faClock as faClockWhite, faCopy } from '@fortawesome/free-regular-svg-icons'
import { faAlarmClock, faHeadSideHeart } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import axios from 'axios'
import VueClipboard from 'vue-clipboard2'
import { createGtm } from '@gtm-support/vue-gtm'
import Nav from '@/components/Navbar.vue'
import Spinner from '@/components/Spinner.vue'
import NewAlert from '@/components/NewAlert.vue'
import NewConfirm from '@/components/NewConfirm.vue'
import {fireAnalyticsSetup} from '@/composables/FirebaseAnalytics.js'
import { useRouteHistory } from '@/composables/History.js'

const {outboundListener} = fireAnalyticsSetup()
// import { showConfirmDialog } from '@/components/NewConfirm.vue'

// Vue 3 says don't use 'Vue' but create an app and use that.

const vueapp = createApp({
  ...App
})

// use the router definition in router.js
vueapp.use(approuter)

//Add navigated paths to history stack for back navigation
approuter.afterEach(
  (to, from) => {
    const {historyStack} = useRouteHistory()
    //If from and to path is not the same OR this is the first entry
    //Push to history
    if(to.path !== from.path || historyStack.value.length === 0) {
      historyStack.value.push(to)
    }
  }
)


if (window.cordova) {
  //Setup Firebase analytics call for page_view after navigating to a new route
  approuter.afterEach((to, from) => {
      window.FirebasePlugin.logEvent("page_view", { page_path: to.path, page_title: to.name });
  })
  //Set listener for any clicks on anchor elements and send data to Firebase analytics
  outboundListener()
}

// Add Font Awesome icons.
library.add(faAlarmClock)
library.add(faArrowLeft)
library.add(faBaby)
library.add(faArrowRight)
library.add(faBars)
library.add(faBell)
library.add(faBicycle)
library.add(faBookReader)
library.add(faBuilding)
library.add(faCalendarAlt)
library.add(faCalendarPlus)
library.add(faCamera)
library.add(faCar)
library.add(faChevronCircleLeft)
library.add(faChevronCircleRight)
library.add(faChevronDown)
library.add(faChevronLeft)
library.add(faChevronRight)
library.add(faCircle)
library.add(faClock)
library.add(faClockWhite)
library.add(faClose)
library.add(faCoffee)
library.add(faCog)
library.add(faComment)
library.add(faDesktop)
library.add(faDownload)
library.add(faEllipsisH)
library.add(faEnvelope)
library.add(faExclamationCircle)
library.add(faExternalLink)
library.add(faFileSignature)
library.add(faHeadSideHeart)
library.add(faHome)
library.add(faInfoCircle)
library.add(faLaptop)
library.add(faLink)
library.add(faMapMarkerAlt)
library.add(faMinus)
library.add(faPhoneAlt)
library.add(faPlayCircle)
library.add(faPlus)
library.add(faPrint)
library.add(faPoundSign)
library.add(faQuestionCircle)
library.add(faRestroom)
library.add(faSearch)
library.add(faShower)
library.add(faSignOutAlt)
library.add(faTimesCircle)
library.add(faUser)
library.add(faVoteYea)
library.add(faWalking)
library.add(faWheelchair)
library.add(faWifi)
library.add(faWrench)
library.add(faX)
library.add(faUsers)
// library.add(faUsersClass) // this one requires a pro license
library.add(faHeartbeat)
library.add(faIcons)
library.add(faTint)
library.add(faSync)
library.add(faMedkit)
library.add(faCalendarCheck)
library.add(faLocationDot)
library.add(faCheck)
library.add(faDiamondTurnRight)
library.add(faArrowUpRightFromSquare)
library.add(faCopy)
library.add(faLocationCrosshairs)
library.add(faElevator)
vueapp.component('font-awesome-icon', FontAwesomeIcon)
  .component('Nav', Nav)
  .component('Spinner', Spinner)
  .component('NewAlert', NewAlert)
  .component('NewConfirm', NewConfirm)
// ummm... TODO? see [link]
// Vue.config.productionTip = false

// Intialise cookies
vueapp.use(VueCookies)

// Initialise Axios to use env url, retain login credentials and use global error handler

axios.defaults.baseURL = process.env.VUE_APP_WS_URL
axios.defaults.withCredentials = true
axios.interceptors.response.use((response) => response, (error) => {
  let msg = error
  if (error.response) {
    if (error.response.data) {
      if (error.response.status === 403) {
        // username or password is wrong so we get 403. check authinterceptor
        msg = msg.response.data.error
        if (msg != null) {
          if (typeof window.errorOverride === 'undefined') {
            alert(msg)
          } else {
            window.errorOverride(msg)
          }
        }
      } else if (error.response.status === 401) {
        // Need to re-authenticate e.g. if app left open overnight.
        approuter.push('login')
      } else if (error.response.status === 500) { // allow the user to see the (hopefully sanitised) error message
        msg = msg.response.data.error
        if (msg != null) {
          if (typeof window.errorOverride === 'undefined') {
            const d = new Date()
            const datetime = d.toISOString().split('T')

            alert(msg + '\nDate and Time of error: ' + datetime[0] + ', ' + datetime[1])
          } else {
            window.errorOverride(msg)
          }
        }
      } else {
        approuter.push('/maintenance')
      }
    } else {
      // uh oh something else has happened, so lets check connectivity to internet.
      approuter.push('/maintenance')
    }
  }
  throw error
})

// Initialise Vue clipboard for copying links
vueapp.use(VueClipboard)

// Initialse Google Analytics
//GTM Plugin
//https://www.npmjs.com/package/@gtm-support/vue-gtm
if (!window.cordova) {
  vueapp.use(
    createGtm({
      id: 'GTM-PLBCJPZ9',
      vueRouter: approuter,
      vueRouterAdditionalEventData: () => ({
        gtmPagePath: approuter.currentRoute._value.path,
        gtmPageTitle: approuter.currentRoute._value.name
      }),
    }),
  );
}

// Global methods.
vueapp.mixin({
  methods: {
    go (path) {
      this.$router.push(path)
    }
  }
})

vueapp.mount('#app')
